import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getCompanyProfile } from '../../queries/companyProfile';

const FAILURE_FALLBACK = 'Company Profile processing failed. Please try again.';

const applyGeneratingProfile = (current, profile, sources) => {
  const nextProfile = {
    ...(current?.profile || {}),
    ...profile,
    status: 'generating',
    generationStep: profile?.generationStep || 'sources',
    buildError: '',
    sections: [],
  };

  if (!current) {
    return { profile: nextProfile, sources: sources || [] };
  }

  return {
    ...current,
    profile: nextProfile,
    sources: sources || current.sources,
  };
};

const useProfileProcessingModal = ({ profile, isGenerating, isCreating, createProfile }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [allComplete, setAllComplete] = useState(false);
  const sawGenerating = useRef(false);
  const startedUpdatedAt = useRef(null);
  const requestPending = useRef(false);

  const begin = () => {
    startedUpdatedAt.current = profile?.updatedAt || null;
    sawGenerating.current = false;
    requestPending.current = true;
    setError('');
    setAllComplete(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!isGenerating) return;
    sawGenerating.current = true;
    setError('');
    setOpen(true);
  }, [isGenerating]);

  useEffect(() => {
    if (!open || error || allComplete || isCreating || isGenerating || requestPending.current) return;
    if (profile?.status !== 'ready' && profile?.status !== 'failed') return;

    const updatedAt = profile?.updatedAt || null;
    const profileChanged = Boolean(updatedAt && updatedAt !== startedUpdatedAt.current);
    if (!sawGenerating.current && !profileChanged) return;

    sawGenerating.current = false;
    startedUpdatedAt.current = null;

    if (profile.status === 'failed') {
      setError(profile.buildError || FAILURE_FALLBACK);
      return;
    }

    setAllComplete(true);
  }, [open, error, allComplete, isCreating, isGenerating, profile?.status, profile?.updatedAt, profile?.buildError]);

  useEffect(() => {
    if (!allComplete) return undefined;
    const timer = setTimeout(() => {
      setOpen(false);
      setAllComplete(false);
      toast.success('Your Company Profile is ready.');
    }, 700);
    return () => clearTimeout(timer);
  }, [allComplete]);

  const start = async () => {
    begin();
    try {
      const response = await createProfile({});
      const bundle = response?.data;
      if (bundle?.profile?.status === 'generating') {
        sawGenerating.current = true;
        queryClient.setQueryData(getCompanyProfile().queryKey, (current) =>
          applyGeneratingProfile(current, bundle.profile, bundle.sources),
        );
      }
    } catch (requestError) {
      sawGenerating.current = false;
      startedUpdatedAt.current = null;
      setError(requestError?.response?.data?.message || FAILURE_FALLBACK);
    } finally {
      requestPending.current = false;
    }
  };

  const closeError = () => {
    setOpen(false);
    setError('');
  };

  return {
    open,
    error,
    allComplete,
    isRetrying: isCreating,
    start,
    retry: start,
    closeError,
  };
};

export default useProfileProcessingModal;
